import { FlutterChannel } from "../enums/channel";
import { hasSupportForNativeShorts } from "../capabilities";
import { DEFAULT_SHORT_MAX_DURATION_SECONDS } from "@/constants/shorts";

type PendingTransfer = {
  transferId: string;
  totalChunks: number;
  totalBytes: number;
  receivedChunks: number;
  byteArrays: (Uint8Array | null)[];
  timeoutId: ReturnType<typeof setTimeout> | null;
};

let activeTransfer: PendingTransfer | null = null;

// Callback definitions
export type ShortRecordedCallback = (file: File) => void;
export type ShortTransferStartCallback = (payload: {
  transferId: string;
  totalChunks: number;
  totalBytes: number;
}) => void;
export type ShortTransferProgressCallback = (payload: {
  transferId: string;
  progress: number;
  totalChunks: number;
  receivedChunks: number;
}) => void;
export type ShortTransferErrorCallback = (payload: {
  transferId: string;
  error: string;
}) => void;
export type ShortCameraDismissedCallback = () => void;

const recordedListeners = new Set<ShortRecordedCallback>();
const startListeners = new Set<ShortTransferStartCallback>();
const progressListeners = new Set<ShortTransferProgressCallback>();
const errorListeners = new Set<ShortTransferErrorCallback>();
const dismissedListeners = new Set<ShortCameraDismissedCallback>();

let inFlightPromiseResolve: ((file: File | null) => void) | null = null;
let inFlightPromiseReject: ((reason: Error) => void) | null = null;

const TRANSFER_INACTIVITY_TIMEOUT_MS = 30000;

function resetTransferTimeout(): void {
  if (!activeTransfer) return;
  if (activeTransfer.timeoutId) {
    clearTimeout(activeTransfer.timeoutId);
  }
  const currentId = activeTransfer.transferId;
  activeTransfer.timeoutId = setTimeout(() => {
    if (activeTransfer && activeTransfer.transferId === currentId) {
      console.warn(
        `Short video transfer ${currentId} timed out due to inactivity.`,
      );
      handleShortTransferError(
        currentId,
        "Transfer timed out due to inactivity",
      );
    }
  }, TRANSFER_INACTIVITY_TIMEOUT_MS);
}

function clearInFlightPromise(): void {
  inFlightPromiseResolve = null;
  inFlightPromiseReject = null;
}

export function onShortRecorded(callback: ShortRecordedCallback): () => void {
  recordedListeners.add(callback);
  return () => {
    recordedListeners.delete(callback);
  };
}

export function onShortTransferStart(
  callback: ShortTransferStartCallback,
): () => void {
  startListeners.add(callback);
  return () => {
    startListeners.delete(callback);
  };
}

export function onShortTransferProgress(
  callback: ShortTransferProgressCallback,
): () => void {
  progressListeners.add(callback);
  return () => {
    progressListeners.delete(callback);
  };
}

export function onShortTransferError(
  callback: ShortTransferErrorCallback,
): () => void {
  errorListeners.add(callback);
  return () => {
    errorListeners.delete(callback);
  };
}

export function onShortCameraDismissed(
  callback: ShortCameraDismissedCallback,
): () => void {
  dismissedListeners.add(callback);
  return () => {
    dismissedListeners.delete(callback);
  };
}

export type RequestNativeShortOptions = {
  maxDurationSeconds?: number;
};

export function requestNativeShortCamera(
  options?: RequestNativeShortOptions,
): Promise<File | null> {
  return new Promise((resolve, reject) => {
    if (!hasSupportForNativeShorts()) {
      resolve(null);
      return;
    }

    inFlightPromiseResolve = resolve;
    inFlightPromiseReject = reject;

    const maxDuration =
      options?.maxDurationSeconds ?? DEFAULT_SHORT_MAX_DURATION_SECONDS;
    const channel = (window as any)[FlutterChannel.CAMERA];
    if (channel && typeof channel.postMessage === "function") {
      channel.postMessage(
        JSON.stringify({
          action: "recordShort",
          maxDurationSeconds: maxDuration,
        }),
      );
    } else {
      clearInFlightPromise();
      resolve(null);
    }
  });
}

export function abortActiveShortTransfer(): void {
  if (activeTransfer) {
    if (activeTransfer.timeoutId) {
      clearTimeout(activeTransfer.timeoutId);
    }
    const transferId = activeTransfer.transferId;
    activeTransfer = null;
    if (inFlightPromiseResolve) {
      inFlightPromiseResolve(null);
      clearInFlightPromise();
    }
    errorListeners.forEach((callback) => {
      try {
        callback({ transferId, error: "Transfer aborted by user" });
      } catch (err) {
        console.error("Error in onShortTransferError listener:", err);
      }
    });
  }
}

export function handleShortTransferStart(
  transferId: string,
  totalChunks: number,
  totalBytes: number,
): void {
  if (activeTransfer && activeTransfer.timeoutId) {
    clearTimeout(activeTransfer.timeoutId);
  }

  activeTransfer = {
    transferId,
    totalChunks,
    totalBytes,
    receivedChunks: 0,
    byteArrays: new Array(totalChunks).fill(null),
    timeoutId: null,
  };

  resetTransferTimeout();

  startListeners.forEach((callback) => {
    try {
      callback({ transferId, totalChunks, totalBytes });
    } catch (err) {
      console.error("Error in onShortTransferStart listener:", err);
    }
  });
}

export function handleShortTransferChunk(
  transferId: string,
  chunkIndex: number,
  chunkBase64: string,
): void {
  if (!activeTransfer || activeTransfer.transferId !== transferId) {
    return;
  }

  resetTransferTimeout();

  if (chunkIndex >= 0 && chunkIndex < activeTransfer.totalChunks) {
    if (!activeTransfer.byteArrays[chunkIndex]) {
      try {
        const binaryString = atob(chunkBase64);
        const chunkBytes = new Uint8Array(binaryString.length);
        for (let j = 0; j < binaryString.length; j++) {
          chunkBytes[j] = binaryString.charCodeAt(j);
        }
        activeTransfer.byteArrays[chunkIndex] = chunkBytes;
        activeTransfer.receivedChunks++;
      } catch (e) {
        console.error(`Failed to decode base64 chunk ${chunkIndex}:`, e);
        handleShortTransferError(
          transferId,
          `Failed to decode chunk ${chunkIndex}`,
        );
        return;
      }
    }

    const progress =
      activeTransfer.totalChunks > 0
        ? activeTransfer.receivedChunks / activeTransfer.totalChunks
        : 1;

    progressListeners.forEach((callback) => {
      try {
        callback({
          transferId,
          progress,
          totalChunks: activeTransfer!.totalChunks,
          receivedChunks: activeTransfer!.receivedChunks,
        });
      } catch (err) {
        console.error("Error in onShortTransferProgress listener:", err);
      }
    });
  }
}

export function handleShortTransferComplete(transferId: string): void {
  if (!activeTransfer || activeTransfer.transferId !== transferId) {
    return;
  }

  if (activeTransfer.timeoutId) {
    clearTimeout(activeTransfer.timeoutId);
  }

  try {
    const hasMissingChunks = activeTransfer.byteArrays.some((chunk) => !chunk);
    if (hasMissingChunks) {
      console.error(
        `Failed to reconstruct recorded video file: missing chunk(s) in transfer ${transferId}`,
      );
      handleShortTransferError(transferId, "Missing chunks during transfer");
      return;
    }

    const validByteArrays = activeTransfer.byteArrays as Uint8Array[];
    const blob = new Blob(validByteArrays as BlobPart[], { type: "video/mp4" });
    const file = new File([blob], `short-${Date.now()}.mp4`, {
      type: "video/mp4",
      lastModified: Date.now(),
    });

    if (inFlightPromiseResolve) {
      inFlightPromiseResolve(file);
      clearInFlightPromise();
    }

    recordedListeners.forEach((callback) => {
      try {
        callback(file);
      } catch (err) {
        console.error("Error in onShortRecorded listener:", err);
      }
    });
  } catch (e) {
    console.error("Failed to reconstruct recorded video file:", e);
    handleShortTransferError(
      transferId,
      e instanceof Error ? e.message : String(e),
    );
  } finally {
    activeTransfer = null;
  }
}

export function handleShortTransferError(
  transferId: string,
  error: string,
): void {
  if (activeTransfer && activeTransfer.transferId === transferId) {
    if (activeTransfer.timeoutId) {
      clearTimeout(activeTransfer.timeoutId);
    }
    activeTransfer = null;
  }

  if (inFlightPromiseReject) {
    inFlightPromiseReject(new Error(error));
    clearInFlightPromise();
  }

  errorListeners.forEach((callback) => {
    try {
      callback({ transferId, error });
    } catch (err) {
      console.error("Error in onShortTransferError listener:", err);
    }
  });
}

export function handleShortCameraDismissed(): void {
  if (activeTransfer) {
    if (activeTransfer.timeoutId) {
      clearTimeout(activeTransfer.timeoutId);
    }
    activeTransfer = null;
  }

  if (inFlightPromiseResolve) {
    inFlightPromiseResolve(null);
    clearInFlightPromise();
  }

  dismissedListeners.forEach((callback) => {
    try {
      callback();
    } catch (err) {
      console.error("Error in onShortCameraDismissed listener:", err);
    }
  });
}
